import { Box, Dialog, HStack, Stack } from '@chakra-ui/react';
import { setupI18n, type Messages } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useState } from 'react';

import { InviteLanding } from '#modules/finder/features/posts/components/Post/InviteLanding';
import { DeleteAccountDialogBody } from '#modules/user/features/auth/components/DeleteAccountButton';
import { SignInWithOAuth } from '#modules/user/features/auth/components/SignInWithOAuth';
import { OAuthProvider } from '#modules/user/features/auth/constants/providers';
import { SignInMethods } from '#modules/user/features/profile/components/Settings/SignInMethods';
import { DeleteReason } from '#features/deletion/types';
import { Button, PageHeading, Panel, SectionHeading } from '#features/ui';
import { DialogContent, DialogHeader, DialogRoot } from '#features/ui/components/Dialog';
import { envConfig } from '#constants/envConfig';

const rejectFixtureNetwork = async (): Promise<never> => {
  throw new Error('Apple preview never accesses the network');
};

const USER_ID = 'apple-auth-preview';
export const Route = createFileRoute('/{-$locale}/_layout/preview/apple-auth')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    previewLocale: search.previewLocale === 'ru' ? 'ru' : 'en',
    state:
      search.state === 'deletion-instructions'
        ? 'deletion-instructions'
        : search.state === 'invitation'
          ? 'invitation'
          : search.state === 'linked-disabled'
            ? 'linked-disabled'
            : search.state === 'hidden'
              ? 'hidden'
              : 'available',
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ location, deps }) => {
    const { messages } =
      deps.previewLocale === 'ru'
        ? await import('#features/i18n/locales/ru/messages.po')
        : await import('#features/i18n/locales/en/messages.po');
    return { href: location.href, messages, ...deps };
  },
  component: AppleAuthPreview,
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
});

function AppleAuthPreview() {
  const data = Route.useLoaderData();
  return <IsolatedPreview key={`${data.state}:${data.previewLocale}`} {...data} />;
}
function IsolatedPreview({
  href,
  messages,
  previewLocale,
  state,
}: {
  href: string;
  messages: Messages;
  previewLocale: string;
  state: string;
}) {
  const [i18n] = useState(() =>
    setupI18n({ locale: previewLocale, messages: { [previewLocale]: messages } })
  );
  const [client] = useState(() => {
    // Fixture identities and provider controls never touch the real app cache or network.
    const queryClient = new QueryClient();
    const defaults = queryClient.defaultQueryOptions.bind(queryClient);
    const mutations = queryClient.defaultMutationOptions.bind(queryClient);
    queryClient.defaultQueryOptions = (options) => ({
      ...defaults(options),
      enabled: false,
      staleTime: Infinity,
      queryFn: rejectFixtureNetwork,
      retry: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
    queryClient.defaultMutationOptions = (options) => ({
      ...mutations(options),
      mutationFn: rejectFixtureNetwork,
      retry: false,
    });
    queryClient.setQueryData(['apple-sign-in-availability'], {
      available: state === 'available' || state === 'invitation',
      schemaSupported: true,
    });
    queryClient.setQueryData(['invite-link', 'apple-preview-invite'], {
      status: 'JOINED',
      acceptedCount: 2,
    });
    queryClient.setQueryData(['me'], {
      __typename: 'CurrentUser',
      id: USER_ID,
      displayName: 'Preview',
      oauth: {},
    });
    queryClient.setQueryData(
      ['apple-oauth-account', USER_ID],
      state === 'linked-disabled'
        ? {
            id: 'preview-subject',
            email: 'player-preview@privaterelay.appleid.com',
            displayName: null,
          }
        : null
    );
    for (const provider of Object.values(OAuthProvider)) {
      queryClient.setQueryData(
        ['oauth-redirect-link', provider, new URL(`${envConfig.HOST}${href}`).toString()],
        '#'
      );
    }
    return queryClient;
  });
  return (
    <I18nProvider i18n={i18n}>
      <QueryClientProvider client={client}>
        <Box
          px={4}
          py={8}
          onClickCapture={(event) => {
            if ((event.target as HTMLElement).closest('a[href="#"]')) event.preventDefault();
          }}
        >
          <Stack gap={8} w="md" maxW="100%" mx="auto">
            <PreviewControls state={state} locale={previewLocale} />
            {state === 'deletion-instructions' ? (
              <DeletionInstructionsFixture />
            ) : state === 'invitation' ? (
              <InviteLanding
                isAuth={false}
                onJoined={() => undefined}
                invite={{
                  code: 'apple-preview-invite',
                  sessionId: 'preview-session',
                  gameId: null,
                  inviterUserId: 'preview-inviter',
                  inviterDisplayName: 'Александр Teamplay',
                  inviterAvatar: null,
                  startedAt: '2026-10-03T18:00:00Z',
                  expiredAt: '2026-10-03T20:00:00Z',
                  teamLimit: 5,
                  acceptedCount: 2,
                  freeSeats: 3,
                  status: 'JOINED',
                }}
              />
            ) : (
              <SignInWithOAuth />
            )}
            <Panel variant="object" p={4}>
              <Stack gap={4}>
                <SectionHeading>
                  {previewLocale === 'ru' ? 'Способы входа' : 'Sign-in methods'}
                </SectionHeading>
                <SignInMethods />
              </Stack>
            </Panel>
          </Stack>
        </Box>
      </QueryClientProvider>
    </I18nProvider>
  );
}
function PreviewControls({ state, locale }: { state: string; locale: string }) {
  const { t } = useLingui();
  return (
    <Stack gap={3}>
      <PageHeading align="center">{t`signIn.welcome`}</PageHeading>
      <Box display="flex" gap={2} flexWrap="wrap">
        <Button asChild size="sm" variant="outline">
          <a href={`?state=${state}&previewLocale=${locale === 'ru' ? 'en' : 'ru'}`}>RU / EN</a>
        </Button>
        {['available', 'hidden', 'linked-disabled', 'invitation', 'deletion-instructions'].map(
          (next) => (
            <Button asChild key={next} size="sm" variant="outline">
              <a href={`?state=${next}&previewLocale=${locale}`}>{next}</a>
            </Button>
          )
        )}
      </Box>
    </Stack>
  );
}

function DeletionInstructionsFixture() {
  const { t } = useLingui();
  const [open, setOpen] = useState(true);
  const [reason, setReason] = useState<DeleteReason | undefined>();
  const [comment, setComment] = useState('');
  return (
    <>
      <SectionHeading>iOS deletion instructions fixture (no account deletion)</SectionHeading>
      <Button onClick={() => setOpen(true)}>{t`settings.account.deleteAccount`}</Button>
      <DialogRoot open={open} onOpenChange={(event) => setOpen(event.open)} size="xs">
        <DialogContent>
          <DialogHeader title={t`settings.account.deleteAccount`} />
          <Dialog.Body>
            <DeleteAccountDialogBody
              reason={reason}
              onReasonChange={setReason}
              comment={comment}
              onCommentChange={setComment}
              showAppleInstructions
            />
          </Dialog.Body>
          <Dialog.Footer>
            <HStack>
              <Button onClick={() => setOpen(false)} variant="ghost">{t`form.cancel`}</Button>
              <Button colorPalette="red">{t`Delete`}</Button>
            </HStack>
          </Dialog.Footer>
        </DialogContent>
      </DialogRoot>
    </>
  );
}
